import React, { useState, useRef } from "react";
import INSTRUCTIONS_MARKDOWN from "./instructionsContent";
import "./Instructionscdmqb.css";

const renderInline = (text, keyPrefix) =>
  text
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, i) =>
      part.length > 4 && part.startsWith("**") && part.endsWith("**") ? (
        <strong key={`${keyPrefix}-b${i}`}>{part.slice(2, -2)}</strong>
      ) : (
        part
      ),
    );

const buildListTree = (items, start, level) => {
  const nodes = [];
  let i = start;
  while (i < items.length) {
    const item = items[i];
    if (item.level < level) break;
    if (item.level === level) {
      nodes.push({ text: item.text, children: [] });
      i++;
    } else {
      const [children, next] = buildListTree(items, i, item.level);
      if (nodes.length) nodes[nodes.length - 1].children = children;
      else nodes.push(...children);
      i = next;
    }
  }
  return [nodes, i];
};

const renderList = (nodes, keyPrefix) => (
  <ul className="instructionsListCDMQB" key={keyPrefix}>
    {nodes.map((node, i) => (
      <li key={`${keyPrefix}-${i}`}>
        {renderInline(node.text, `${keyPrefix}-${i}`)}
        {node.children.length > 0 &&
          renderList(node.children, `${keyPrefix}-${i}-ul`)}
      </li>
    ))}
  </ul>
);

// Each "## " section (its heading plus everything up to the next "## " or
// "# ") is wrapped in a bordered block, like the Lab Panels items. The "# "
// title stays outside the blocks.
const renderMarkdown = (markdown) => {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const listLine = /^(\s*)- (.*)$/;
  const output = [];
  let section = null;
  let keyCount = 0;
  let i = 0;

  const nextKey = () => `md-${keyCount++}`;
  const push = (el) => (section ? section.children.push(el) : output.push(el));
  const closeSection = () => {
    if (!section) return;
    output.push(
      <div key={section.key} className="instructionsSectionCDMQB">
        {section.children}
      </div>,
    );
    section = null;
  };

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i++;
      continue;
    }

    const heading = line.match(/^(#{1,3})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      const key = nextKey();
      const Tag = `h${level + 2}`; // # -> h3, ## -> h4, ### -> h5
      const el = (
        <Tag key={key} className="instructionsHeadingCDMQB">
          {renderInline(heading[2], key)}
        </Tag>
      );

      if (level === 1) {
        closeSection();
        output.push(el);
      } else if (level === 2) {
        closeSection();
        section = { key: `${key}-section`, children: [el] };
      } else {
        push(el);
      }
      i++;
      continue;
    }

    if (listLine.test(line)) {
      const items = [];
      while (i < lines.length && listLine.test(lines[i])) {
        const [, indent, text] = lines[i].match(listLine);
        items.push({ level: Math.floor(indent.length / 2), text });
        i++;
      }
      const [tree] = buildListTree(items, 0, items[0].level);
      push(renderList(tree, nextKey()));
      continue;
    }

    const paragraph = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^#{1,3}\s/.test(lines[i]) &&
      !listLine.test(lines[i])
    ) {
      paragraph.push(lines[i].trim());
      i++;
    }
    const key = nextKey();
    push(
      <p key={key} className="instructionsParagraphCDMQB">
        {renderInline(paragraph.join(" "), key)}
      </p>,
    );
  }

  closeSection();
  return output;
};

const RENDERED_INSTRUCTIONS = renderMarkdown(INSTRUCTIONS_MARKDOWN);

/* ------------------------------------------------------------------------- */

export default function InstructionsCDMQB(props) {
  const [position, setPosition] = useState(null);
  const dragOffset = useRef(null);
  const containerRef = useRef(null);

  const startDrag = (e) => {
    // Don't start a drag from the exit button (or any button).
    if (e.target.closest(".instructionsExitCDMQB, button")) return;
    const rect = containerRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const drag = (e) => {
    if (!dragOffset.current) return;
    setPosition({
      x: e.clientX - dragOffset.current.x,
      y: e.clientY - dragOffset.current.y,
    });
  };

  const endDrag = () => {
    dragOffset.current = null;
  };

  return (
    <div
      className={`instructionsPageWrapperCDMQB ${
        props.displayInstructions ? "" : "hideInstructionsPopupCDMQB"
      }`}
    >
      <div
        className="instructionsBackdropCDMQB"
        onClick={props.closeInstructions}
      />
      <div
        ref={containerRef}
        className="instructionsContainerCDMQB"
        role="dialog"
        aria-label="Instructions"
        style={
          position
            ? { left: position.x, top: position.y, transform: "none" }
            : undefined
        }
      >
        <div
          className="instructionsHeaderWrapperCDMQB"
          onPointerDown={startDrag}
          onPointerMove={drag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <div className="instructionsHeaderTitleCDMQB">
            <img
              className="instructionsHeaderIconCDMQB"
              src="/exhibit.png"
              alt=""
              draggable={false}
            />
            <h4 className="instructionsHeaderCDMQB">Instructions</h4>
          </div>
          <div
            className="instructionsExitCDMQB"
            onClick={props.closeInstructions}
            aria-label="Close instructions"
            role="button"
          ></div>
        </div>
        <div className="instructionsContentWrapperCDMQB">
          <div className="instructionsBodyCDMQB">{RENDERED_INSTRUCTIONS}</div>
        </div>
      </div>
    </div>
  );
}
